import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * ComboBox — shared dropdown widget used everywhere the app needs a
 * "select-with-typeahead" experience.
 *
 * Props:
 *   value           current value (string)
 *   onChange(v)     called whenever the value changes
 *   options         either an array of strings ["A","B"] or an array of
 *                   { value, label } objects. Mixed not supported.
 *   placeholder     placeholder shown in the input when value === ""
 *   allowAdd        default true. When false, brand-new typed values are NOT
 *                   accepted: the chevron behaves like a closed <select>.
 *   id / testid     hooked into the input + dropdown list for tests.
 *   className       optional extra classes for the wrapper.
 *
 * Behaviour:
 *   - Chevron toggles the dropdown.
 *   - Focusing the input opens the dropdown.
 *   - Filters as the user types. Exact-match (case-insensitive) shows the
 *     FULL list so EDIT-mode pre-populated values let the user browse.
 *   - "+ Add <value>" row appears when allowAdd is true and the typed
 *     value isn't already in the list.
 *   - Click outside closes the dropdown.
 */
export default function ComboBox({
  id,
  testid,
  value,
  onChange,
  options = [],
  placeholder,
  allowAdd = true,
  className = "",
  disabled = false,
  maxResults = 0, // 0 = no cap. >0 = cap the dropdown to N results.
  hideAddOption = false, // when true, suppresses the "+ Add …" row even if allowAdd is true (useful for search bars where any free-text is accepted but offering "+ Add" is noisy).
  onAdd, // optional (value: string) => void — fires whenever the user
         // creates a brand-new entry through the "+ Add" affordance
         // (click on the Add row, OR Enter on a typed value not in the
         // option set). Used by callers that want to show a one-time
         // "Added X to FieldName" toast on first creation.
  optimisticSet, // optional Set<string> of values that exist ONLY in the
                 // current browser session (added via "+ Add" but not yet
                 // persisted by a save). Matching options render an
                 // italic ★ next to the label. Comparison is
                 // case-insensitive to mirror the option-pool dedupe.
  menuClassName = "", // optional extra classes appended to the dropdown
                      // <ul>. Useful for overriding the default max-height
                      // (max-h-56) on menus that need more room without
                      // affecting every ComboBox instance.
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Normalise options into [{value, label}] internally.
  const normalised = (options || []).map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );

  // Resolve the current label so the input shows the friendly name when
  // options are { value, label }.
  // Defensive coercion: `value` may be a number (e.g. Vintage=2014) or
  // even an array left over from a previous category schema when the
  // user swaps categories in Edit mode. Passing a non-string to
  // `q.trim()` below would throw `TypeError: q.trim is not a function`
  // and unmount the whole form (blocking Save entirely). Normalise to
  // a string here — arrays become comma-joined so the field still
  // shows *something* meaningful until the user picks a new value.
  const currentEntry = normalised.find((o) => o.value === value);
  const rawDisplay = currentEntry ? currentEntry.label : value;
  let displayValue;
  if (rawDisplay === null || rawDisplay === undefined) {
    displayValue = "";
  } else if (Array.isArray(rawDisplay)) {
    displayValue = rawDisplay.map((v) => String(v ?? "")).filter(Boolean).join(", ");
  } else {
    displayValue = String(rawDisplay);
  }

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  const q = typeof displayValue === "string" ? displayValue : String(displayValue ?? "");
  const trimmed = q.trim();
  const exactMatch = normalised.some(
    (o) => o.label.toLowerCase() === trimmed.toLowerCase()
  );
  // Filter only while typing something that isn't already an option. Empty
  // input OR exact match → surface the FULL list (typical of EDIT mode where
  // the field is pre-populated).
  const filteredRaw = trimmed === "" || exactMatch
    ? normalised
    : normalised.filter((o) => o.label.toLowerCase().includes(trimmed.toLowerCase()));
  const filtered = maxResults > 0 ? filteredRaw.slice(0, maxResults) : filteredRaw;
  const truncated = maxResults > 0 && filteredRaw.length > maxResults;
  const showAdd = allowAdd && !hideAddOption && trimmed.length > 0 && !exactMatch;

  const pick = (entry) => {
    onChange(entry.value);
    setOpen(false);
  };

  const safeTestid = testid || "combo";

  // Lower-cased lookup for the optimistic ★ marker. Built once per render
  // from the parent-supplied Set (typed Set/Iterable). Empty when absent.
  const optimisticLower = optimisticSet
    ? new Set([...optimisticSet].map((v) => String(v).toLowerCase()))
    : null;
  const isOptimistic = (v) =>
    optimisticLower ? optimisticLower.has(String(v).toLowerCase()) : false;

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          id={id}
          data-testid={safeTestid}
          type="text"
          value={q}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            const v = e.target.value;
            if (allowAdd) {
              onChange(v);
            } else {
              // For closed sets, only commit if it matches a known label.
              const match = normalised.find(
                (o) => o.label.toLowerCase() === v.toLowerCase()
              );
              if (match) onChange(match.value);
              // Otherwise, swallow but keep the dropdown open so the user can
              // navigate the list.
            }
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (allowAdd) {
                // Treat Enter on a non-empty, non-matching typed value as
                // a creation so the host can fire its onAdd notification
                // (e.g. the one-time "+ Add" toast in ItemEntry).
                if (trimmed && !exactMatch && onAdd) onAdd(trimmed);
                onChange(trimmed);
              }
              setOpen(false);
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          className="vp-input pr-9"
          placeholder={placeholder}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={`${safeTestid}-list`}
          // Closed sets: discourage manual typing — let the chevron drive
          // selection. We still accept typing for accessibility.
          readOnly={!allowAdd && false}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label="Toggle options"
          data-testid={`${safeTestid}-toggle`}
          onClick={() => !disabled && setOpen((p) => !p)}
          disabled={disabled}
          className="absolute right-0 inset-y-0 px-2 flex items-center vp-muted hover:text-[hsl(var(--fg))] disabled:opacity-40"
        >
          <ChevronDown
            size={14}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      {open && (filtered.length > 0 || showAdd) && (
        <ul
          role="listbox"
          id={`${safeTestid}-list`}
          data-testid={`${safeTestid}-list`}
          className={`absolute z-30 mt-1 left-0 right-0 max-h-96 overflow-auto rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))] shadow-lg vp-fade-in py-1 ${menuClassName}`}
        >
          {filtered.map((o) => (
            <li
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              data-testid={`${safeTestid}-opt-${o.value}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(o);
              }}
              className={`px-3 py-1.5 text-xs cursor-pointer hover:bg-[hsl(var(--muted))] ${
                o.value === value ? "bg-[hsl(var(--muted))]" : ""
              }`}
            >
              {o.label}
              {isOptimistic(o.value) && (
                <span
                  data-testid={`${safeTestid}-opt-${o.value}-optimistic`}
                  title="Added this session — pending save"
                  aria-label="Session-only — not yet saved"
                  className="ml-1.5 italic vp-muted text-[10px] align-baseline"
                >
                  ★
                </span>
              )}
            </li>
          ))}
          {showAdd && (
            <li
              role="option"
              data-testid={`${safeTestid}-add`}
              onMouseDown={(e) => {
                e.preventDefault();
                if (onAdd) onAdd(trimmed);
                onChange(trimmed);
                setOpen(false);
              }}
              className="px-3 py-1.5 text-xs cursor-pointer hover:bg-[hsl(var(--muted))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] border-t border-[hsl(var(--border))]"
            >
              + Add &ldquo;{trimmed}&rdquo;
            </li>
          )}
          {truncated && (
            <li
              data-testid={`${safeTestid}-truncated`}
              className="px-3 py-1 text-[10px] uppercase tracking-wider vp-muted border-t border-[hsl(var(--border))] pointer-events-none"
            >
              Showing top {maxResults} of {filteredRaw.length} matches — keep typing to narrow.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
